import { wallClockIn, type CurrencyCode } from '@bbpos/types';
import { amountToMajor, formatAmount, getSubunitMultiplier } from '@bbpos/utils';
import { getLocationTimezone } from './tenant-clock';

/**
 * Format a minor-unit amount as a currency string, defaulting to INR.
 *
 * Thin wrapper over the shared, subunit-aware {@link formatAmount} from
 * `@bbpos/utils` — there is no independent `/100` or hardcoded-currency logic
 * here any more. INR output is byte-identical to the previous
 * `Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' })`
 * implementation, including its leading-minus convention for negative amounts
 * (`-₹1.00`, not `₹-1.00`); the only logic kept locally is that sign
 * placement, which is not currency-specific.
 *
 * The first parameter is still named `paise` and the call signature is
 * unchanged (a single positional argument), so existing POS call sites
 * compile without edits. `currency` is an optional opt-in for currency-aware
 * callers; subunit factor, symbol and decimals all come from the currency.
 */
export function formatCurrency(paise: number, currency: CurrencyCode = 'INR'): string {
  return paise < 0 ? `-${formatAmount(-paise, currency)}` : formatAmount(paise, currency);
}

/** "lowest – highest", or one price when they are equal (a variant parent's selling price). */
export function formatPriceRange(min: number, max: number, currency: CurrencyCode = 'INR'): string {
  return min === max
    ? formatCurrency(min, currency)
    : `${formatCurrency(min, currency)} – ${formatCurrency(max, currency)}`;
}

/**
 * Derive a currency's display symbol from `@bbpos/utils` without duplicating the
 * symbol table here. `formatAmount` is defined as `symbol + amountToMajor`, so
 * stripping the formatted-amount suffix yields exactly the symbol the rest of
 * the codebase uses (`₹` for INR, ISO-code fallbacks for the GCC set). Mirrors
 * the admin shim (`apps/admin/src/utils/currency.ts`) so the two frontends share
 * one recipe.
 */
function currencySymbol(currency: CurrencyCode): string {
  const amount = amountToMajor(0, currency);
  return formatAmount(0, currency).slice(0, -amount.length);
}

/**
 * Converts the smallest unit (paise for INR) to a formatted string with the
 * currency symbol. Defaults to INR.
 *
 * Sibling of {@link formatCurrency} kept as a distinct named export purely so
 * the ~5 POS call sites still importing `formatPaise` from the deprecated,
 * INR-hardcoded `@bbpos/utils` shim can migrate with a name-preserving import
 * swap (`@bbpos/utils` → `@/lib/format`) per the Axis-1 recipe. Behaviour and
 * INR golden output are identical to the admin `@/utils/currency#formatPaise`
 * (`formatAmount(paise, 'INR')` — 2-decimal, `en-IN` lakh grouping).
 */
export function formatPaise(paise: number, currency: CurrencyCode = 'INR'): string {
  return formatAmount(paise, currency);
}

/**
 * Short-form currency for compact displays (e.g. stat cards). Byte-identical to
 * the admin shim's `formatPaiseCompact`: INR keeps the exact Indian lakh (L) /
 * thousand (K) compaction; non-INR uses generic K/M with the currency symbol.
 * The subunit divisor is sourced from `@bbpos/utils`, not a hardcoded `/100`,
 * so 3-decimal currencies compact correctly.
 */
export function formatPaiseCompact(paise: number, currency: CurrencyCode = 'INR'): string {
  const major = paise / getSubunitMultiplier(currency);

  if (currency === 'INR') {
    if (major >= 10_00_000) return `₹${(major / 100_000).toFixed(1)}L`;
    if (major >= 1_000) return `₹${(major / 1_000).toFixed(1)}K`;
    return formatPaise(paise, currency);
  }

  const symbol = currencySymbol(currency);
  if (major >= 1_000_000) return `${symbol}${(major / 1_000_000).toFixed(1)}M`;
  if (major >= 1_000) return `${symbol}${(major / 1_000).toFixed(1)}K`;
  return formatPaise(paise, currency);
}

/**
 * Format an ISO 8601 date string as DD/MM/YYYY HH:MM on `timeZone`'s clock —
 * by default the till's active store (else the tenant's), never this device's:
 * an IST laptop in a Muscat store must show Muscat times (PS2-002).
 */
export function formatDateTime(iso: string, timeZone: string = getLocationTimezone()): string {
  const d = wallClockIn(timeZone, new Date(iso));
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

/**
 * Format a Date as YYYY-MM-DD for API query params.
 */
export function formatDateParam(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
